import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  Ban,
  ArrowLeft,
  Info,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface TenantComplianceDetailProps {
  tenantId: string;
  onClose: () => void;
  onSuspend: () => void;
}

export const TenantComplianceDetail: React.FC<TenantComplianceDetailProps> = ({
  tenantId,
  onClose,
  onSuspend,
}) => {
  const { data: detailData, isLoading, error } = useQuery({
    queryKey: ['server-compliance-tenant', tenantId],
    queryFn: async () => {
      const response = await api.serverAdmin.getTenantComplianceDetails(tenantId);
      return response.data;
    },
  });

  const handleExportReport = async () => {
    try {
      toast.info('Generating tenant compliance report...');
      const response = await api.compliance.getReport();
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tenant-compliance-report-${tenantId}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Tenant compliance report downloaded');
    } catch (err: any) {
      console.error('Failed to export report:', err);
      toast.error('Failed to export tenant compliance report');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'good':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'good':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load tenant compliance details. Please try again.
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  const metrics30 = detailData?.metrics_30_days;
  const metrics7 = detailData?.metrics_7_days;
  const tenantInfo = detailData?.tenant_info;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Tenant Compliance Details
          </DialogTitle>
          <DialogDescription>
            {tenantInfo?.name || 'Tenant'} - Compliance Metrics and Status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Tenant Info */}
          {tenantInfo && (
            <Card>
              <CardHeader>
                <CardTitle>Tenant Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{tenantInfo.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Domain</p>
                    <p className="font-medium">{tenantInfo.domain || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant="outline">{tenantInfo.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {tenantInfo.created_at
                        ? format(new Date(tenantInfo.created_at), 'PPp')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Overall Status */}
          <Card>
            <CardHeader>
              <CardTitle>Overall Compliance Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {getStatusIcon(metrics30?.compliance_status)}
                <Badge className={getStatusColor(metrics30?.compliance_status) + ' text-lg px-4 py-1'}>
                  {metrics30?.compliance_status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </div>

              {metrics30?.warnings && metrics30.warnings.length > 0 && (
                <div className="mt-4 space-y-2">
                  {metrics30.warnings.map((warning: string, idx: number) => (
                    <Alert
                      key={idx}
                      variant={warning.includes('CRITICAL') ? 'destructive' : 'default'}
                    >
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{warning}</AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Opt-out Rate (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(metrics30?.opt_out_rate || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics30?.opt_outs || 0} opt-outs
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Spam Complaints (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(metrics30?.spam_complaint_rate || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics30?.spam_complaints || 0} complaints
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Delivery Rate (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(metrics30?.delivery_rate || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics30?.messages_delivered || 0} delivered
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Consent Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(metrics30?.consent_rate || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics30?.contacts_with_consent || 0} of {metrics30?.total_contacts || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Opt-outs */}
          {detailData?.recent_opt_outs && detailData.recent_opt_outs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Opt-outs (Last 7 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contact</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailData.recent_opt_outs.map((optOut: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          {optOut.phone || optOut.email || 'Unknown'}
                        </TableCell>
                        <TableCell>{format(new Date(optOut.date), 'PPp')}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{optOut.reason}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {detailData?.recommendations && detailData.recommendations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {detailData.recommendations.map((recommendation: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">{recommendation}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button variant="outline" onClick={handleExportReport}>
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
            {(metrics30?.compliance_status === 'critical' || metrics30?.compliance_status === 'warning') && (
              <Button variant="destructive" onClick={onSuspend}>
                <Ban className="h-4 w-4 mr-2" />
                Suspend Tenant
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
